import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Combobox,
  TextInput,
  Loader,
  useCombobox,
  CloseButton,
  Text,
  Stack,
  Group,
} from "@mantine/core";
import { IconGasStation, IconMapPin, IconHistory } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import Fuse, { IFuseOptions } from "fuse.js";

import { usePlaceAutocomplete } from "../hooks/usePlaceAutocomplete";
import { PlaceData } from "../hooks/useGoogleMapsAutocomplete";
import { GeoCoords } from "../hooks/useGeolocation";

import { useAppEnvironment } from "../AppEnvironmentContext";
import { useLocationHistoryContext } from "../LocationHistoryContext";

import { PlacePickerModal } from "./PlacePickerModal";
import { DistanceUnitSystem, formatDistance } from "./placeAutocompleteUtils";

// ============================================
// Constants
// ============================================

/** Min characters to trigger search */
const MIN_SEARCH_LENGTH = 2;

/** Max recent places to show on focus (before any typing) */
const MAX_RECENT_ON_FOCUS = 5;

/** Max recent places to show while typing (leave room for Google results) */
const MAX_RECENT_WHILE_TYPING = 3;

/** Fuse.js threshold — lower = stricter matching */
const FUSE_THRESHOLD = 0.4;

// ============================================
// Types
// ============================================

export interface PlaceAutocompleteInputProps {
  /** Current input value (place name) */
  value: string;

  /**
   * Callback when value changes
   * @param name - The place name (for display in input)
   * @param placeData - Full place data including address (null if typed manually)
   */
  onChange: (name: string, placeData: PlaceData | null) => void;

  /**
   * Previously selected / saved places to show in the dropdown.
   * Shown as a separate group above Google search results.
   * On focus (before typing): shows up to 5 items in their original order.
   * While typing: fuzzy-matched against name and address, up to 3 results.
   */
  recentPlaces?: PlaceData[];

  /**
   * Location bias for relevant nearby results.
   * If provided, used as initial bias. The component will also
   * silently request geolocation on focus/open and override this
   * with the user's actual position once available.
   */
  locationBias?: GeoCoords | null;

  /** Place types to search for (default: ['establishment']) */
  placeTypes?: string[];

  /** Placeholder text */
  placeholder?: string;

  /** Input label */
  label?: string;

  /** Input size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Error message */
  error?: string | null | undefined;

  /** Whether field is required */
  required?: boolean;

  /** Whether field is disabled */
  disabled?: boolean;

  radiusMeters?: number | undefined;

  /**
   * Callback when input is focused.
   * Called in addition to the component's internal geolocation request.
   */
  onFocus?: () => void;

  /** Whether to render dropdown in a portal (fixes issues inside modals/drawers) */
  withinPortal?: boolean;

  /**
   * Whether to show distance from the user's location next to each suggestion.
   * Requires locationBias to be set so Google API returns distanceMeters.
   * Default: true
   */
  showDistance?: boolean;

  /**
   * Unit system for distance display.
   * "km" (default): shows meters for <1km, km with 1 decimal for >=1km
   * "mi": shows feet for <0.1mi (~160m), miles with 1 decimal for >=0.1mi
   */
  distanceUnit?: DistanceUnitSystem;
}

// ============================================
// Fuse.js configuration
// ============================================

const FUSE_OPTIONS: IFuseOptions<PlaceData> = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "address.address1", weight: 0.15 },
    { name: "address.displayName", weight: 0.15 },
  ],
  threshold: FUSE_THRESHOLD,
  includeScore: false,
};

// ============================================
// Main Component
// ============================================

export const PlaceAutocompleteInput: React.FC<PlaceAutocompleteInputProps> = (props) => {
  const { isMobile } = useAppEnvironment();

  if (isMobile) {
    return <PlaceAutocompleteMobile {...props} />;
  }

  return <PlaceAutocompleteDesktop {...props} />;
};

// ============================================
// Mobile Sub-Component: Read-only input + full-screen modal
// ============================================

const PlaceAutocompleteMobile: React.FC<PlaceAutocompleteInputProps> = ({
  value,
  onChange,
  recentPlaces = [],
  locationBias = null,
  placeTypes = ["establishment"],
  placeholder = "Search for a place or address",
  label = "Place",
  size = "md",
  error,
  required,
  disabled,
  radiusMeters,
  showDistance = true,
  distanceUnit = "km",
}) => {
  const { t } = useTranslation("common");
  const [modalOpened, setModalOpened] = useState(false);

  const handleModalSelect = useCallback(
    (name: string, placeData: PlaceData | null) => {
      onChange(name, placeData);
    },
    [onChange]
  );

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("", null);
  }, [onChange]);

  const hasValue = value && value.length > 0;

  const placeIcon =
    placeTypes.includes("gas_station") ? (
      <IconGasStation size={18} style={{ opacity: 0.6 }} />
    ) : (
      <IconMapPin size={18} style={{ opacity: 0.6 }} />
    );

  const getRightSection = () => {
    if (hasValue && !disabled) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          aria-label={t("placeAutocomplete.clearPlace", "Clear place")}
        />
      );
    }
    return null;
  };

  return (
    <>
      <TextInput
        label={label}
        placeholder={placeholder}
        value={value}
        readOnly
        onClick={() => {
          if (!disabled) {
            setModalOpened(true);
          }
        }}
        leftSection={hasValue ? placeIcon : undefined}
        rightSection={getRightSection()}
        rightSectionPointerEvents={hasValue && !disabled ? "auto" : "none"}
        error={error}
        required={required}
        disabled={disabled}
        size={size}
        styles={{
          input: {
            cursor: disabled ? "not-allowed" : "pointer",
            caretColor: "transparent",
          },
        }}
      />

      {/* Modal handles geolocation internally on open */}
      <PlacePickerModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onSelect={handleModalSelect}
        initialValue={value}
        recentPlaces={recentPlaces}
        locationBias={locationBias}
        placeTypes={placeTypes}
        radiusMeters={radiusMeters}
        placeholder={placeholder}
        showDistance={showDistance}
        distanceUnit={distanceUnit}
      />
    </>
  );
};

// ============================================
// Desktop Sub-Component: Standard Combobox with dropdown
// ============================================

const PlaceAutocompleteDesktop: React.FC<PlaceAutocompleteInputProps> = ({
  value,
  onChange,
  recentPlaces = [],
  locationBias = null,
  placeTypes = ["establishment"],
  placeholder = "Search for a place or address",
  label = "Place",
  size = "md",
  error,
  required,
  disabled,
  radiusMeters,
  onFocus,
  withinPortal = false,
  showDistance = true,
  distanceUnit = "km",
}) => {
  const { t } = useTranslation("common");

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const {
    placePredictions,
    loading,
    fetchPlacePredictions,
    fetchPlaceDetails,
    clearPredictions,
  } = usePlaceAutocomplete({ includedTypes: placeTypes, radiusMeters });

  const {
    requestLocation,
  } = useLocationHistoryContext();

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Store locationBias in ref to use latest value in callbacks
  const locationBiasRef = useRef(locationBias);
  useEffect(() => {
    locationBiasRef.current = locationBias;
  }, [locationBias]);

  // Track whether we've already requested geolocation to avoid repeated prompts
  const geoRequestedRef = useRef(false);

  // Ref to current input value so async geolocation callback can read it
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // ============================================
  // Fuse.js index — rebuilt only when recentPlaces changes
  // ============================================

  const fuseIndex = useMemo(() => {
    if (recentPlaces.length === 0) return null;
    return new Fuse(recentPlaces, FUSE_OPTIONS);
  }, [recentPlaces]);

  // ============================================
  // Filtered recent places based on current input
  // ============================================

  const filteredRecentPlaces = useMemo(() => {
    if (recentPlaces.length === 0) return [];

    const query = value?.trim() || "";

    if (query.length === 0) {
      return recentPlaces.slice(0, MAX_RECENT_ON_FOCUS);
    }

    if (!fuseIndex) return [];

    const results = fuseIndex.search(query);
    return results
      .slice(0, MAX_RECENT_WHILE_TYPING)
      .map((result) => result.item);
  }, [recentPlaces, fuseIndex, value]);

  /**
   * Silently request geolocation once.
   * Updates locationBiasRef and re-fetches predictions if the user
   * has already started typing.
   */
  const requestGeolocationOnce = useCallback(() => {
    if (geoRequestedRef.current) return;
    geoRequestedRef.current = true;

    requestLocation({
      reverseGeocode: false,
      allowPrompt: true,
    })
      .then((result) => {
        if (result?.coords) {
          locationBiasRef.current = result.coords;

          // Re-fetch with location bias if the user has already typed something
          const currentValue = valueRef.current;
          if (currentValue && currentValue.trim().length >= MIN_SEARCH_LENGTH) {
            fetchPlacePredictions(currentValue, result.coords);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to acquire location for place search:", err);
      });
  }, [requestLocation, fetchPlacePredictions]);

  /**
   * Handle input text change
   */
  const handleInputChange = useCallback(
    (newValue: string) => {
      onChange(newValue, null);

      if (newValue && newValue.trim().length >= MIN_SEARCH_LENGTH) {
        fetchPlacePredictions(newValue, locationBiasRef.current);
        combobox.openDropdown();
      } else if (newValue && filteredRecentPlaces.length > 0) {
        clearPredictions();
        combobox.openDropdown();
      } else {
        clearPredictions();
        combobox.closeDropdown();
      }

      combobox.updateSelectedOptionIndex();
    },
    [onChange, fetchPlacePredictions, clearPredictions, combobox, filteredRecentPlaces.length]
  );

  /**
   * Handle clear button click
   */
  const handleClear = useCallback(() => {
    onChange("", null);
    clearPredictions();
    combobox.closeDropdown();
  }, [onChange, clearPredictions, combobox]);

  /**
   * Handle recent place selection — no API call needed
   */
  const handleRecentPlaceSelect = useCallback(
    (place: PlaceData) => {
      onChange(place.name || "", place);
      combobox.closeDropdown();
    },
    [onChange, combobox]
  );

  /**
   * Handle Google place selection from dropdown
   */
  const handleGooglePlaceSelect = useCallback(
    async (placeId: string, placeName: string) => {
      setIsLoadingDetails(true);

      try {
        const placeData = await fetchPlaceDetails(placeId);

        if (placeData) {
          onChange(placeData.name || placeName, placeData);
        } else {
          onChange(placeName, null);
        }
      } catch (err) {
        console.error("Failed to fetch place details:", err);
        onChange(placeName, null);
      } finally {
        setIsLoadingDetails(false);
        combobox.closeDropdown();
      }
    },
    [fetchPlaceDetails, onChange, combobox]
  );

  /**
   * Handle input focus.
   * Silently requests geolocation on first focus so place results
   * are biased to the user's area from the very first keystroke.
   */
  const handleFocus = useCallback(() => {
    onFocus?.();
    requestGeolocationOnce();

    const hasContent =
      filteredRecentPlaces.length > 0 ||
      (value && value.trim().length >= MIN_SEARCH_LENGTH && placePredictions.length > 0);

    if (hasContent) {
      combobox.openDropdown();
    }
  }, [onFocus, requestGeolocationOnce, value, combobox, filteredRecentPlaces.length, placePredictions.length]);

  /**
   * Handle input blur — desktop is reliable, no workarounds needed
   */
  const handleBlur = useCallback(() => {
    combobox.closeDropdown();
  }, [combobox]);

  /**
   * Handle option submit
   */
  const handleOptionSubmit = useCallback(
    (val: string) => {
      if (val.startsWith("recent:")) {
        const index = parseInt(val.slice(7), 10);
        const place = filteredRecentPlaces[index];
        if (place) {
          handleRecentPlaceSelect(place);
        }
        return;
      }

      const prediction = placePredictions.find((p) => p.placeId === val);
      if (prediction) {
        handleGooglePlaceSelect(prediction.placeId, prediction.name);
      }
    },
    [filteredRecentPlaces, placePredictions, handleRecentPlaceSelect, handleGooglePlaceSelect]
  );

  const hasValue = value && value.length > 0;
  const hasRecentPlaces = filteredRecentPlaces.length > 0;
  const hasGooglePredictions = placePredictions.length > 0;
  const showDropdown = hasRecentPlaces || hasGooglePredictions;

  /**
   * Determine right section content
   */
  const getRightSection = useCallback(() => {
    if (loading || isLoadingDetails) {
      return <Loader size={18} />;
    }
    if (hasValue && !disabled) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          aria-label={t("placeAutocomplete.clearPlace", "Clear place")}
        />
      );
    }
    return null;
  }, [loading, isLoadingDetails, hasValue, disabled, size, handleClear, t]);

  /**
   * Get icon for place type
   */
  const placeIcon =
    placeTypes.includes("gas_station") ? (
      <IconGasStation size={16} style={{ opacity: 0.6 }} />
    ) : (
      <IconMapPin size={16} style={{ opacity: 0.6 }} />
    );

  /**
   * Render distance badge
   */
  const renderDistance = useCallback(
    (distanceMeters: number | null | undefined) => {
      if (!showDistance || distanceMeters == null || distanceMeters < 0) {
        return null;
      }
      return (
        <Text
          size="xs"
          c="dimmed"
          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {formatDistance(distanceMeters, distanceUnit)}
        </Text>
      );
    },
    [showDistance, distanceUnit]
  );

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      disabled={disabled}
      withinPortal={withinPortal}
      zIndex={withinPortal ? 10000 : undefined}
      position="bottom"
      middlewares={{ flip: true, shift: true }}
      floatingStrategy={withinPortal ? "fixed" : "absolute"}
    >
      <Combobox.Target>
        <TextInput
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={(event) => handleInputChange(event.currentTarget.value)}
          onClick={() => {
            if (hasValue && (hasRecentPlaces || hasGooglePredictions)) {
              combobox.openDropdown();
            } else if (!hasValue && hasRecentPlaces) {
              combobox.openDropdown();
            }
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          error={error}
          required={required}
          disabled={disabled}
          rightSection={getRightSection()}
          size={size}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {/* No results */}
          {!showDropdown && hasValue && value.length >= MIN_SEARCH_LENGTH && !loading && (
            <Combobox.Empty>
              {t("placeAutocomplete.noPlacesFound", "No places found")}
            </Combobox.Empty>
          )}

          {/* Minimum characters */}
          {!showDropdown && hasValue && value.length < MIN_SEARCH_LENGTH && !loading && (
            <Combobox.Empty>
              {t("placeAutocomplete.typeAtLeastNChars", "Type at least {{count}} characters to search", { count: MIN_SEARCH_LENGTH })}
            </Combobox.Empty>
          )}

          {/* Recent places group */}
          {hasRecentPlaces && (
            <Combobox.Group label={t("placeAutocomplete.recentPlaces", "Recent Places")}>
              {filteredRecentPlaces.map((place, index) => (
                <Combobox.Option
                  value={`recent:${index}`}
                  key={`recent-${place.name}-${index}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                    }}
                  >
                    <div style={{ paddingTop: 2 }}>
                      <IconHistory size={16} style={{ opacity: 0.5 }} />
                    </div>
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Text size="md" fw={500} truncate>
                        {place.name}
                      </Text>
                      {place.address?.address1 && (
                        <Text size="xs" c="dimmed" truncate>
                          {place.address.address1}
                          {place.address.city && `, ${place.address.city}`}
                        </Text>
                      )}
                    </Stack>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {/* Google predictions group */}
          {hasGooglePredictions && (
            <Combobox.Group
              label={hasRecentPlaces ? t("placeAutocomplete.searchResults", "Search Results") : undefined}
            >
              {placePredictions.map((prediction) => (
                <Combobox.Option
                  value={prediction.placeId}
                  key={prediction.placeId}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                    }}
                  >
                    <div style={{ paddingTop: 2 }}>{placeIcon}</div>
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap" justify="space-between">
                        <Text size="md" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
                          {prediction.name}
                        </Text>
                        {renderDistance(prediction.distanceMeters)}
                      </Group>
                      {prediction.address && (
                        <Text size="xs" c="dimmed" truncate>
                          {prediction.address}
                        </Text>
                      )}
                    </Stack>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};